import { type PropsWithChildren, useEffect } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { type CVAnalysisResultParams } from '@/app/cv/result/cvAnalysisResult';
import { ErrorPage } from '@/components/ErrorPage';
import { LoaderFull } from '@/components/LoaderFull';
import { CVAnalysisKeys, CV_PATH } from '@/features/cv-analysis';

import { ACCOUNT_PATH } from '../account/constants';
import { useSecurityTokens } from './useAuth';
import { useGetAccountQuery } from './useAuthQueries';

export const GuardCVAnalysis: React.FC<PropsWithChildren> = ({ children }) => {
  const [searchParams] = useSearchParams();
  const [, setToken] = useSecurityTokens();
  const account = useGetAccountQuery();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const params = useParams<CVAnalysisResultParams>();

  const token = searchParams?.get('token');
  const email = searchParams?.get('email');
  const { cvId } = params;

  useEffect(() => {
    if (token) {
      setToken({ jwtToken: token, refreshToken: '' });

      if (email && cvId) {
        const queryKey = CVAnalysisKeys.CVAnalysisResultQuery({
          email,
          id: cvId,
        });

        queryClient.removeQueries({
          queryKey,
          exact: true,
        });
      }
    }
  }, [token, email, cvId, setToken, queryClient]);

  if (account?.error?.response?.status === 401 && !token) {
    navigate(`${ACCOUNT_PATH}/login?redirect=${CV_PATH}`, {
      replace: true,
    });
    return;
  }

  if ((!account || account.isError) && !token) {
    return <ErrorPage errorCode={account?.error?.response?.status} />;
  }

  if (account.data) return children;

  return <LoaderFull />;
};
